import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../../../helpers';
import {
    ID,
    IOrderSale,
    RECONCILIATION_ONLY,
    REQUIRE_CREDIT_NOTE,
    REQUIRE_INVOICE_CODE,
    REQUIRE_SUPPLEMENT,
} from './order-sale-form';
import { useOrderSaleOrderSaleReceiptTypeInputQuery } from '../../../../../../services';
import { useSetOrderSaleTaxRequirement } from './use-set-order-sale-tax-requirement';

const ACCOUNT: keyof IOrderSale = 'account';
const DATE: keyof IOrderSale = 'date';
const RECEIPT_TYPE_ID: keyof IOrderSale = 'receipt_type_id';
const EXPECTED_PAYMENT_DATE: keyof IOrderSale = 'expected_payment_date';

// Pre-fills sale fields from the selected client account's defaults. Credit
// days derive expected_payment_date only while creating; edits preserve the
// loaded date and only re-derive the receipt-aware document requirements.
export function usePrefillFromClientAccount() {
    const rx = useFormContext();
    const { data } = useOrderSaleOrderSaleReceiptTypeInputQuery();
    const { setOrderSaleTaxRequirement } =
        useSetOrderSaleTaxRequirement();

    // A non-taxable receipt cannot carry a complemento or a nota de crédito, so
    // the account defaults for those must not turn them back on.
    const receiptAppliesTax = (whenUnset = true) => {
        const receiptTypeId = rx.getValues(
            RECEIPT_TYPE_ID,
        ) as IOrderSale['receipt_type_id'];
        const receiptType = (data?.getReceiptTypes ?? []).find(
            (type) => type.id === receiptTypeId,
        );
        return receiptType ? receiptType.applies_tax : whenUnset;
    };

    const setPaymentDate = (
        account: IOrderSale['account'],
        date?: IOrderSale['date'],
    ) => {
        if (rx.getValues(ID) || !account || !date) return;
        const creditDays = Number(account.client_credit_days ?? 0);
        if (creditDays === 0) return;
        rx.setValue(
            EXPECTED_PAYMENT_DATE,
            dayjs(date).add(creditDays, 'day').format(DATE_FORMAT),
        );
    };

    const prefillFromAccount = (account: IOrderSale['account']) => {
        if (!account) {
            rx.setValue(REQUIRE_INVOICE_CODE, false);
            setOrderSaleTaxRequirement(false, {
                accountAllowsAutomaticTaxCalculation: false,
            });
            return;
        }
        // Seed this sale's reconciliation_only from the client account's
        // default. Only runs on an explicit account pick (this is the picker's
        // onChange), so it never clobbers a saved value on open.
        rx.setValue(
            RECONCILIATION_ONLY,
            !!account.client_reconciliation_only,
        );
        const appliesTax = receiptAppliesTax();
        rx.setValue(
            REQUIRE_CREDIT_NOTE,
            appliesTax && !!account.client_require_credit_note,
        );
        rx.setValue(
            REQUIRE_SUPPLEMENT,
            appliesTax && !!account.client_require_supplement,
        );
        rx.setValue(
            REQUIRE_INVOICE_CODE,
            receiptAppliesTax(false) &&
                !!account.client_requires_invoice_code,
        );
        setOrderSaleTaxRequirement(
            receiptAppliesTax(false) && !!account.client_requires_tax,
            {
                accountAllowsAutomaticTaxCalculation:
                    !!account.client_automatic_tax_calculation,
            },
        );
        setPaymentDate(account, rx.getValues(DATE) as IOrderSale['date']);
    };

    const recomputePaymentDate = (date?: IOrderSale['date']) => {
        setPaymentDate(rx.getValues(ACCOUNT) as IOrderSale['account'], date);
    };

    return { prefillFromAccount, recomputePaymentDate };
}
